import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { apiFetch, UnauthorizedError } from "./lib/api";
import { consumeRedirectState, initMsal } from "./lib/msal";
import { queryKeys } from "./lib/queries";
import { Spinner } from "./components/ui";
import { ToastProvider } from "./components/toast";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // A session that expired (or was revoked when an admin deactivated the
      // user) surfaces as a 401 on the next request: drop the cached identity
      // and bounce to the login page.
      if (error instanceof UnauthorizedError) {
        queryClient.setQueryData(queryKeys.me, undefined);
        if (!window.location.pathname.startsWith("/login")) {
          void router.navigate({
            to: "/login",
            search: { redirect: window.location.pathname + window.location.search },
          });
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        error instanceof UnauthorizedError ? false : failureCount < 2,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient, user: null },
  defaultPreload: "intent",
  defaultPendingComponent: () => <Spinner />,
  /*
   * Don't show the pending component for a load that finishes quickly.
   *
   * The pending component *replaces* the whole route, so on Timesheets, Reports
   * and Chase every filter change tore down the page header, the filter bar and
   * the tab strip, flashed a small centred spinner, then rebuilt them — the
   * controls the user was actively working in disappeared under their cursor.
   * Most of those loads return in well under 400ms, so the honest fix is to not
   * render a loading state for them at all. `defaultPendingMinMs` then keeps the
   * spinner up long enough to read whenever it does appear, rather than
   * strobing.
   */
  defaultPendingMs: 400,
  defaultPendingMinMs: 400,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("#root is missing from index.html");

/**
 * Configure MSAL before rendering, from the API's runtime config (S1.1). If we
 * are returning from a sign-in that carried an invite token in its `state`,
 * activate it here (S2.2) so the pending account becomes real before the first
 * guarded route runs.
 */
async function bootstrap(): Promise<void> {
  let config: Awaited<ReturnType<typeof apiFetch<"GET /api/auth/config">>> = {
    configured: false,
    devSignin: false,
  };
  try {
    config = await apiFetch("GET /api/auth/config");
  } catch {
    // No config endpoint (or it failed): render anyway — guarded routes will
    // bounce to /login, which will show that sign-in is unavailable.
  }

  if (config.configured) {
    await initMsal({
      authority: config.authority,
      clientId: config.clientId,
      scopes: config.scopes,
    });

    const inviteToken = consumeRedirectState();
    if (inviteToken) {
      try {
        const { user } = await apiFetch("POST /api/invite/activate", {
          body: { token: inviteToken },
        });
        queryClient.setQueryData(queryKeys.me, { user });
      } catch {
        // Activation failed (wrong email, expired, transient). Say so here: MSAL
        // may have brought the browser back to `/activate`, but that page stands
        // down for a token bootstrap handled (handledAtBootstrap), so this is the
        // only place that can tell the user.
        void router.navigate({ to: "/login", search: { error: "activation" } });
      }
    }
  }

  // ToastProvider sits inside the query client (Undo issues mutations) but
  // *outside* Suspense and the router: a route that suspends must not unmount a
  // live toast, and a confirmation for something that just happened has to
  // survive the navigation it usually triggers.
  createRoot(rootElement!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Suspense fallback={<Spinner />}>
            <RouterProvider router={router} />
          </Suspense>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}

void bootstrap();
